// NPM Imports
import { cloneDeep } from "lodash";

// Internal Imports
import { COMMON } from "../constants";
import { GlobalUtils } from "../utils";
import {
  generateColumDef,
  generateDTRowsFromProductCatalogs,
  generateRowData,
  handleAddingProductsToDealTable,
  handleNetPriceRounding,
  handleRippleEffectOnCellChange,
} from "./parser";

// Destructuring
const { getAppFlagValue } = GlobalUtils;
const { APP_FLAGS, ROUNDING_TYPE } = COMMON;

export const generateGridData = (
  rawColumnData,
  rawRowData,
  rawDiscountData,
  tableType,
) => {
  const readOnlyData = {
    rawColumnData,
    rawRowData,
    tableType,
  };

  const payload = {
    break: false,
    columnDefs: [
      {
        draggable: "false",
        field: "sno",
        headerName: "#",
        hidden: false,
        minWidth: 20,
        width: 20,
        name: "serial_no",
        pinned: true,
        position: 0,
      },
    ], // Generate columns definitions, behaviour of each columns will be based on this.
    rowData: [], // Stores the final row data
    discount: cloneDeep(rawDiscountData), // Stores the group level & deal level discount data.
    addedCatalogIds: [], // Stores the catalog product id's which are already added to dealtable => [5,16]
    props: {}, // Stores the dropdown options of the singleSelect/multiSelect columns.
    metaData: {
      apiRequiredFields: ["id"], // Stores FLDXXX of direct columns which Backend API needs
      derivedColumn: { sourceTable: "", sourceField: "" }, // Stores the linked table id and which Field(FLDXX) has to be picked
      fieldMapIndex: {}, // Store the filed name map => {sku: FLDxWnEYKHlO4zM}
      linkedTables: [], // Stores TBLXXX & FLDXXX's of the columns which are linked from different tables.
      masterTblID: rawColumnData.VIW.masterTblID || null,
      tableId: rawColumnData.VIW.tableId,
      tblViewId: rawColumnData.VIW.id,
    }, // Holds metadata for the grid
    initialGridLoadFlag: true,
  };

  //* PREVENT MUTATION OF DATA WITHIN THE ARCHITECTURE
  Object.freeze(readOnlyData);
  Object.seal(payload);

  //* METHOD TO GENERATE COLUMN DEFINITIONS
  generateColumDef(readOnlyData, payload);

  //* METHOD TO GENERATE ROW DATA
  if (rawRowData && rawRowData.length > 0) {
    generateRowData(readOnlyData, payload);
  }
  return {
    addedCatalogIds: payload.addedCatalogIds,
    columnDefs: payload.columnDefs,
    discount: payload.discount,
    metaData: payload.metaData,
    props: payload.props,
    rowData: payload.rowData,
  };
};

// ======================================================================

export const reactionsOnAddingProductsToDealTable = (
  productIds,
  flag,
  flagType,
  productCatalogs,
  metaData,
  dealTableId,
  addedCatalogIds,
  discount,
  rowData,
) => {
  const readOnlyData = {
    // Props To Create Row Data
    dealTableId,
    flag,
    flagType,
    metaData,
    productCatalogs,
    productIds,
    roundingType: ROUNDING_TYPE[getAppFlagValue(APP_FLAGS.ROUNDING_FLAG)] || "", // Rounding Type Org Based flag to update netprice on add/update of rows
    // Props To Be Updated After Row Data Is Created
    addedCatalogIds,
    discount,
    rowData,
  };

  const payload = {
    catalogIds: [], // Stores the updated catalog product id's which are added into dealtable => [5,16]
    conversionFactor: 1,
    discount, // Stores the group level & deal level discount data.
    endDate: new Date().toISOString(),
    intermediateValue: 0, // Temp key to be used for calc Ext.Base Price
    productCatalogs: [], // Stores the updated product catalog flags.
    row: {}, // Temp key to store a single row data, this is used in mapProductsToDealTable method.
    rowData: [...rowData], // Stores the final row data
    stopRoundUpReCalc: false, // Flag to prevent repeated roundUp calculation after POT calc inside the architecture
  };

  //* PREVENT MUTATION OF DATA WITHIN THE ARCHITECTURE
  Object.freeze(readOnlyData);
  Object.seal(payload);

  //* METHOD TO CONVERT PLAIN JSON INTO OUR GRID JSON FORMAT
  return generateDTRowsFromProductCatalogs(readOnlyData, payload).then(() => ({
    catalogIds: payload.catalogIds,
    discount: payload.discount,
    productCatalogs: payload.productCatalogs,
    rowData: payload.rowData,
  }));
};

// ======================================================================

export const reactionsOnAddingProductsToDealTableV2 = (
  productIds,
  flag,
  flagType,
  productCatalogs,
  metaData,
  dealTableId,
  pricelistId,
  documentId,
  discount,
  rowData,
) => {
  try {
    const readOnlyData = {
      // Props To Create Row Data
      dealTableId,
      discount,
      documentId,
      flag,
      flagType,
      metaData,
      pricelistId,
      productCatalogs,
      productIds,
      roundingType:
        ROUNDING_TYPE[getAppFlagValue(APP_FLAGS.ROUNDING_FLAG)] || "", // Rounding Type Org Based flag to update netprice on add/update of rows,
      rowData,
    };

    const payload = {
      catalogIds: [], // Stores the updated catalog product id's which are added into dealtable => [5,16]
      discount, // Stores the group level & deal level discount data.
      productCatalogs: [], // Stores the updated product catalog flags.
      rowData: [], // Stores the final row data
    };

    //* PREVENT MUTATION OF DATA WITHIN THE ARCHITECTURE
    Object.freeze(readOnlyData);
    Object.seal(payload);

    //* METHOD TO CONVERT PLAIN JSON INTO OUR GRID JSON FORMAT
    return handleAddingProductsToDealTable(readOnlyData, payload)
      .then(() => ({
        catalogIds: payload.catalogIds,
        discount: payload.discount,
        productCatalogs: payload.productCatalogs,
        rowData: payload.rowData,
      }))
      .catch(error => {
        console.error(
          "Mashup => reactionsOnAddingProductsToDealTableV2",
          error,
        );
        throw error;
      });
  } catch (error) {
    console.error("Mashup => reactionsOnAddingProductsToDealTableV2", error);
    throw error;
  }
};

// ======================================================================

export const reactionsOnCellChange = (
  columnData,
  row,
  rowIndex,
  rowData,
  metaData,
  discount,
  dealTableId,
) => {
  const readOnlyData = {
    columnName: columnData.name,
    dealTableId,
    metaData,
    roundingType: ROUNDING_TYPE[getAppFlagValue(APP_FLAGS.ROUNDING_FLAG)] || "", // Rounding Type Org Based flag to update netprice on add/update of rows,
    rowIndex,
  };

  const payload = {
    break: false,
    conversionFactor: 1,
    discount,
    endDate: new Date().toISOString(),
    intermediateValue: 0, // Temp value to be used for calc Ext.Base Price,
    resetDealDiscountDirtyFlag: false,
    row: { ...row },
    rowData, // Stores the final row data
    stopRoundUpReCalc: false, // Flag to prevent repeated roundUp calculation after POT calc inside the architecture
  };

  //* PREVENT MUTATION OF DATA WITHIN THE ARCHITECTURE
  Object.freeze(readOnlyData);
  Object.seal(payload);

  return handleRippleEffectOnCellChange(readOnlyData, payload).then(() => ({
    discount: payload.discount,
    rowData: payload.rowData,
  }));
};

// ======================================================================

export const reactionsOnNetPriceRounding = (
  rowData,
  metaData,
  discount,
  netPriceFld,
  roundingType,
  dealTableId,
) => {
  const readOnlyData = {
    dealTableId,
    metaData,
    netPriceFld,
    roundingType,
  };
  Object.freeze(readOnlyData);

  const payload = {
    discount,
    rowData, // Stores the final row data
  };
  Object.seal(payload);

  handleNetPriceRounding(readOnlyData, payload);

  return {
    discount: payload.discount,
    rowData: payload.rowData,
  };
};

// ======================================================================



// WEBPACK FOOTER //
// ./src/mashupV4/index.js